import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { 
  BookOpen, ArrowLeft, Loader2, CheckCircle2, XCircle, 
  RefreshCw, Sparkles, DollarSign, Play, Pause, Home,
  FileText, Globe, Languages, Trash2, AlertCircle, Lock,
  Image, Eye, ImagePlus, Activity, ExternalLink
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;
const LMS_PASSWORD = "Lokesh@123";
const VERSION = "V3";
const CACHE_BUSTER = `v=${Date.now()}`;

// These will be replaced with dynamic data from the API
const fallbackExamTypes = [
  { value: "tgt", label: "TGT" },
  { value: "pgt", label: "PGT" },
  { value: "non_teaching", label: "Non-Teaching" }
];

const fallbackSubjectsByExam = {
  tgt: [
    { value: "hindi", label: "Hindi" },
    { value: "english", label: "English" },
    { value: "mathematics", label: "Mathematics" },
    { value: "science", label: "Science" },
    { value: "social_science", label: "Social Science" },
    { value: "computer_science", label: "Computer Science" },
    { value: "physical_education", label: "Physical Education" },
    { value: "music", label: "Music" },
    { value: "art", label: "Art" }
  ],
  pgt: [
    { value: "hindi", label: "Hindi" },
    { value: "english", label: "English" },
    { value: "mathematics", label: "Mathematics" },
    { value: "physics", label: "Physics" },
    { value: "chemistry", label: "Chemistry" },
    { value: "biology", label: "Biology" },
    { value: "commerce", label: "Commerce" },
    { value: "economics", label: "Economics" },
    { value: "geography", label: "Geography" },
    { value: "history", label: "History" },
    { value: "computer_science", label: "Computer Science" },
    { value: "music", label: "Music" },
    { value: "art", label: "Art" }
  ],
  non_teaching: [
    { value: "hostel_warden", label: "Hostel Warden" },
    { value: "library_science", label: "Library Science" }
  ]
};

export default function LMS() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => localStorage.getItem('lms_authenticated') === 'true');
  const [password, setPassword] = useState("");
  const [examType, setExamType] = useState("");
  const [subject, setSubject] = useState("");
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState(new Set());
  const [processingTopics, setProcessingTopics] = useState(new Map()); // topic -> {en: 'loading'|'done'|'error', hi: ...}
  const [totalCredits, setTotalCredits] = useState(0);
  const [bulkCreatingSubject, setBulkCreatingSubject] = useState(null); // {exam_type, subject} - tracks WHICH subject is being bulk created
  const [stats, setStats] = useState(null);
  const [bulkJobProgress, setBulkJobProgress] = useState(null); // {completed, total, credits_used, job_id}
  
  // Dynamic exam types and subjects from API
  const [examTypes, setExamTypes] = useState(fallbackExamTypes);
  const [subjectsByExam, setSubjectsByExam] = useState(fallbackSubjectsByExam);
  
  // Fetch exam types from API on mount
  useEffect(() => {
    const fetchExamTypes = async () => {
      try {
        const response = await axios.get(`${API}/notes/lms/syllabus/exams`, { timeout: 8000 });
        const exams = response.data.exams || [];
        if (exams.length > 0) {
          setExamTypes(exams.map(e => ({ value: e.id, label: e.name })));
        }
      } catch (error) {
        console.error("Failed to fetch exam types:", error);
        // Keep using fallback
      }
    };
    if (isAuthenticated) {
      fetchExamTypes();
    }
  }, [isAuthenticated]);
  
  // Fetch subjects when exam type changes
  useEffect(() => {
    const fetchSubjects = async () => {
      if (!examType) return;
      try {
        const response = await axios.get(`${API}/notes/lms/syllabus/exams/${examType}/subjects`, { timeout: 8000 });
        const subjects = response.data.subjects || [];
        if (subjects.length > 0) {
          setSubjectsByExam(prev => ({
            ...prev,
            [examType]: subjects.map(s => ({ value: s.id, label: s.name }))
          }));
        }
      } catch (error) {
        console.error("Failed to fetch subjects:", error);
        // Keep using fallback
      }
    };
    if (isAuthenticated && examType) {
      fetchSubjects();
    }
  }, [isAuthenticated, examType]);
  const [activeJob, setActiveJob] = useState(null); // For resuming job view after tab close
  const [pendingCount, setPendingCount] = useState(null); // Total pending notes across all subjects
  const [hindiIssues, setHindiIssues] = useState(null); // Hindi notes with English content
  
  // New states for Images features
  const [notesWithoutImages, setNotesWithoutImages] = useState(null);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [addingImages, setAddingImages] = useState(null); // topic being processed

  // Handle login
  const handleLogin = (e) => {
    e.preventDefault();
    if (password === LMS_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem('lms_authenticated', 'true');
      toast.success("Access granted");
    } else {
      toast.error("Invalid password");
    }
  };

  // Fetch notes stats and pending count on load
  useEffect(() => {
    if (isAuthenticated) {
      axios.get(`${API}/notes/stats?${CACHE_BUSTER}`).then(res => setStats(res.data)).catch(() => {});
      axios.get(`${API}/notes/lms/pending-all?${CACHE_BUSTER}`).then(res => setPendingCount(res.data)).catch(() => {});
      axios.get(`${API}/notes/lms/check-hindi-quality?${CACHE_BUSTER}`).then(res => setHindiIssues(res.data)).catch(() => {});
      axios.get(`${API}/notes/lms/notes-without-images?${CACHE_BUSTER}`).then(res => setNotesWithoutImages(res.data)).catch(() => {});
    }
  }, [isAuthenticated]);

  // Poll generation status every 3 seconds
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const pollStatus = async () => {
      try {
        const res = await axios.get(`${API}/notes/lms/generation-status?${CACHE_BUSTER}`);
        setGenerationStatus(res.data);
      } catch (e) {}
    };
    
    pollStatus();
    const interval = setInterval(pollStatus, 3000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Create ALL pending notes across all subjects
  const createAllPendingNotes = async () => {
    if (!pendingCount || pendingCount.total_pending_notes === 0) {
      toast.info("All notes already created!");
      return;
    }
    
    if (!window.confirm(`This will create ${pendingCount.total_pending_notes} notes across all subjects.\n\nEstimated cost: ~$${(pendingCount.total_pending_notes * 0.05).toFixed(2)}\n\nContinue?`)) {
      return;
    }
    
    try {
      const res = await axios.post(`${API}/notes/lms/create-all-pending?${CACHE_BUSTER}`);
      toast.success(`Started creating ${res.data.total_notes} notes across all subjects!`);
      setBulkCreatingSubject({ exam_type: "ALL", subject: "ALL" });
      setBulkJobProgress({ completed: 0, total: res.data.total_notes, credits_used: 0 });
      startJobPolling(res.data.job_id);
    } catch (err) {
      toast.error("Failed to start mega bulk creation");
    }
  };

  // Fix Hindi notes with English content
  const fixHindiNotes = async () => {
    if (!hindiIssues || hindiIssues.notes_with_issues === 0) {
      toast.info("No Hindi notes need fixing!");
      return;
    }
    
    if (!window.confirm(`This will regenerate ${hindiIssues.notes_with_issues} Hindi notes that have English content.\n\nEstimated cost: ~$${(hindiIssues.notes_with_issues * 0.03).toFixed(2)}\n\nContinue?`)) {
      return;
    }
    
    try {
      const res = await axios.post(`${API}/notes/lms/fix-hindi-notes?${CACHE_BUSTER}`);
      toast.success(`Started fixing ${res.data.count} Hindi notes!`);
      setBulkCreatingSubject({ exam_type: "FIX_HINDI", subject: "ALL" });
      setBulkJobProgress({ completed: 0, total: res.data.count, credits_used: 0 });
      startJobPolling(res.data.job_id);
    } catch (err) {
      toast.error("Failed to start Hindi fix");
    }
  };

  // View note - Opens student notes page in new tab
  const viewNote = (topicName, language) => {
    // Build URL with query params for the student notes page
    const params = new URLSearchParams({
      exam: examType,
      subject: subject,
      topic: topicName,
      lang: language,
      preview: 'true' // Flag to indicate LMS preview mode
    });
    
    // Open in new tab - same page students see
    window.open(`/notes?${params.toString()}`, '_blank');
    toast.success(`Opening "${topicName}" in new tab...`);
  };

  // Add images to a specific note
  const addImagesToNote = async (topicName, language) => {
    setAddingImages(`${topicName}-${language}`);
    try {
      const res = await axios.post(`${API}/notes/lms/add-images?${CACHE_BUSTER}`, {
        exam_type: examType,
        subject,
        topic: topicName,
        language
      });
      toast.success(`Added ${res.data.image_count} images to "${topicName}"`);
      // Refresh topics list
      fetchTopics();
      // Refresh notes without images count
      axios.get(`${API}/notes/lms/notes-without-images?${CACHE_BUSTER}`).then(res => setNotesWithoutImages(res.data)).catch(() => {});
    } catch (err) {
      toast.error("Failed to add images");
    } finally {
      setAddingImages(null);
    }
  };

  // Add images to ALL notes without images
  const addImagestoAllNotes = async () => {
    if (!notesWithoutImages || notesWithoutImages.total === 0) {
      toast.info("All notes already have images!");
      return;
    }
    
    if (!window.confirm(`This will add images to ${notesWithoutImages.total} notes.\n\nThis may take some time. Continue?`)) {
      return;
    }
    
    try {
      const res = await axios.post(`${API}/notes/lms/add-images-bulk?${CACHE_BUSTER}`);
      toast.success(`Started adding images to ${res.data.count} notes!`);
      setBulkCreatingSubject({ exam_type: "ADD_IMAGES", subject: "ALL" });
      setBulkJobProgress({ completed: 0, total: res.data.count, credits_used: 0 });
      startJobPolling(res.data.job_id);
    } catch (err) {
      toast.error("Failed to start bulk image addition");
    }
  };

  // Fetch topics when exam/subject changes
  const fetchTopics = useCallback(async () => {
    if (!examType || !subject) return;
    setLoading(true);
    setSelectedTopics(new Set());
    try {
      const res = await axios.get(`${API}/notes/lms/status?${CACHE_BUSTER}`, {
        params: { exam_type: examType, subject }
      });
      setTopics(res.data.topics || []);
    } catch (err) {
      toast.error("Failed to load topics");
    } finally {
      setLoading(false);
    }
  }, [examType, subject]);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  // Job polling function - extracted for reuse
  const startJobPolling = useCallback((jobId) => {
    const pollInterval = setInterval(async () => {
      try {
        const statusRes = await axios.get(`${API}/notes/lms/bulk-status/${jobId}?${CACHE_BUSTER}`);
        const status = statusRes.data;
        
        setBulkJobProgress({
          completed: status.completed || 0,
          total: status.total || 0,
          credits_used: status.credits_used || 0,
          current_topic: status.current_topic,
          job_id: jobId
        });
        setTotalCredits(status.credits_used || 0);
        
        if (status.status === 'completed') {
          clearInterval(pollInterval);
          setBulkCreatingSubject(null);
          setBulkJobProgress(null);
          setActiveJob(null);
          setSelectedTopics(new Set());
          setProcessingTopics(new Map());
          toast.success(`✅ Bulk creation complete! ${status.completed} notes created. Credits: $${status.credits_used?.toFixed(3)}`);
          // Refresh stats
          axios.get(`${API}/notes/stats?${CACHE_BUSTER}`).then(res => setStats(res.data)).catch(() => {});
        }
      } catch (e) {
        // Job might still be starting or finished
      }
    }, 2000);
    
    // Stop polling after 60 minutes max
    setTimeout(() => clearInterval(pollInterval), 60 * 60 * 1000);
    
    return pollInterval;
  }, []);

  // Check for active jobs on page load (for resuming after tab close)
  useEffect(() => {
    if (isAuthenticated) {
      axios.get(`${API}/notes/lms/active-jobs?${CACHE_BUSTER}`).then(res => {
        const jobs = res.data.active_jobs || [];
        if (jobs.length > 0) {
          const job = jobs[0]; // Get first active job
          setActiveJob(job);
          setBulkJobProgress({
            completed: job.completed || 0,
            total: job.total || 0,
            credits_used: job.credits_used || 0,
            current_topic: job.current_topic,
            job_id: job.job_id
          });
          setBulkCreatingSubject({ exam_type: job.exam_type, subject: job.subject });
          setTotalCredits(job.credits_used || 0);
          toast.info(`Found active job: ${job.current_topic || 'Processing...'}`);
          
          // Start polling for this job
          startJobPolling(job.job_id);
        }
      }).catch(() => {});
    }
  }, [isAuthenticated, startJobPolling]);

  // Toggle topic selection
  const toggleTopic = (topic) => {
    const newSet = new Set(selectedTopics);
    if (newSet.has(topic)) {
      newSet.delete(topic);
    } else {
      newSet.add(topic);
    }
    setSelectedTopics(newSet);
  };

  // Select all uncreated topics
  const selectAllUncreated = () => {
    const uncreated = topics.filter(t => !t.english || !t.hindi).map(t => t.topic);
    setSelectedTopics(new Set(uncreated));
  };

  // Clear selection
  const clearSelection = () => setSelectedTopics(new Set());

  // Create single note (both languages in parallel)
  const createNote = async (topic) => {
    const topicData = topics.find(t => t.topic === topic);
    if (!topicData) return;

    const needsEn = !topicData.english;
    const needsHi = !topicData.hindi;

    if (!needsEn && !needsHi) {
      toast.info(`${topic} already has both languages`);
      return;
    }

    // Set loading state
    setProcessingTopics(prev => new Map(prev).set(topic, {
      english: needsEn ? 'loading' : 'done',
      hindi: needsHi ? 'loading' : 'done'
    }));

    let creditsUsed = 0;
    const promises = [];

    if (needsEn) {
      promises.push(
        axios.post(`${API}/notes/lms/create`, {
          exam_type: examType, subject, topic, language: "english"
        }).then(res => {
          creditsUsed += res.data.credits_used || 0;
          setProcessingTopics(prev => {
            const newMap = new Map(prev);
            const current = newMap.get(topic) || {};
            newMap.set(topic, { ...current, english: 'done' });
            return newMap;
          });
        }).catch(() => {
          setProcessingTopics(prev => {
            const newMap = new Map(prev);
            const current = newMap.get(topic) || {};
            newMap.set(topic, { ...current, english: 'error' });
            return newMap;
          });
        })
      );
    }

    if (needsHi) {
      promises.push(
        axios.post(`${API}/notes/lms/create`, {
          exam_type: examType, subject, topic, language: "hindi"
        }).then(res => {
          creditsUsed += res.data.credits_used || 0;
          setProcessingTopics(prev => {
            const newMap = new Map(prev);
            const current = newMap.get(topic) || {};
            newMap.set(topic, { ...current, hindi: 'done' });
            return newMap;
          });
        }).catch(() => {
          setProcessingTopics(prev => {
            const newMap = new Map(prev);
            const current = newMap.get(topic) || {};
            newMap.set(topic, { ...current, hindi: 'error' });
            return newMap;
          });
        })
      );
    }

    await Promise.all(promises);
    setTotalCredits(prev => prev + creditsUsed);
    
    // Update topics list
    setTopics(prev => prev.map(t => 
      t.topic === topic ? { ...t, english: true, hindi: true } : t
    ));
  };

  // Rewrite single note
  const rewriteNote = async (topic, language) => {
    setProcessingTopics(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(topic) || {};
      newMap.set(topic, { ...current, [language]: 'loading' });
      return newMap;
    });

    try {
      const res = await axios.post(`${API}/notes/lms/rewrite`, {
        exam_type: examType, subject, topic, language
      });
      setTotalCredits(prev => prev + (res.data.credits_used || 0));
      toast.success(`Rewritten ${language} notes for ${topic}`);
      setProcessingTopics(prev => {
        const newMap = new Map(prev);
        const current = newMap.get(topic) || {};
        newMap.set(topic, { ...current, [language]: 'done' });
        return newMap;
      });
    } catch {
      toast.error(`Failed to rewrite ${topic}`);
      setProcessingTopics(prev => {
        const newMap = new Map(prev);
        const current = newMap.get(topic) || {};
        newMap.set(topic, { ...current, [language]: 'error' });
        return newMap;
      });
    }
  };

  // Create all selected topics - uses background job that continues even if tab closes
  const createAllSelected = async () => {
    if (selectedTopics.size === 0) {
      toast.error("No topics selected");
      return;
    }
    
    // Track which subject is being bulk created
    setBulkCreatingSubject({ exam_type: examType, subject: subject });
    setBulkJobProgress({ completed: 0, total: selectedTopics.size * 2, credits_used: 0 });
    
    try {
      // Start background bulk creation job
      const res = await axios.post(`${API}/notes/lms/bulk-create?${CACHE_BUSTER}`, {
        exam_type: examType,
        subject,
        topics: Array.from(selectedTopics)
      });
      
      const jobId = res.data.job_id;
      toast.success(`Started bulk creation for ${selectedTopics.size} topics. Runs in background - safe to close tab!`);
      
      // Mark all selected topics as loading
      const newProcessing = new Map(processingTopics);
      selectedTopics.forEach(topic => {
        newProcessing.set(topic, { english: 'loading', hindi: 'loading' });
      });
      setProcessingTopics(newProcessing);
      
      // Start polling
      startJobPolling(jobId);
      
    } catch (err) {
      toast.error("Failed to start bulk creation");
      setBulkCreatingSubject(null);
      setBulkJobProgress(null);
    }
  };

  // Check if current subject is being bulk created
  const isCurrentSubjectBulkCreating = bulkCreatingSubject && 
    bulkCreatingSubject.exam_type === examType && 
    bulkCreatingSubject.subject === subject;

  // Get status icon
  const getStatusIcon = (topicName, language) => {
    const processing = processingTopics.get(topicName);
    if (processing && processing[language] === 'loading') {
      return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
    }
    if (processing && processing[language] === 'error') {
      return <XCircle className="w-4 h-4 text-red-500" />;
    }
    const topicData = topics.find(t => t.topic === topicName);
    if (topicData && topicData[language]) {
      return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    }
    return <XCircle className="w-4 h-4 text-gray-400" />;
  };

  const subjects = subjectsByExam[examType] || [];
  const uncreatedCount = topics.filter(t => !t.english || !t.hindi).length;
  const totalTopics = topics.length;
  const createdCount = topics.filter(t => t.english && t.hindi).length;

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/10 backdrop-blur-xl border-white/20">
          <CardContent className="pt-8 pb-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white">LMS - Notes Manager</h1>
              <p className="text-gray-400 text-sm">Enter password to access</p>
              <Badge className="mt-2 bg-purple-600/30 text-purple-300 border-purple-500/50">{VERSION}</Badge>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder-gray-400"
                data-testid="lms-password-input"
              />
              <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" data-testid="lms-login-btn">
                <Lock className="w-4 h-4 mr-2" />
                Access LMS
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Link to="/" className="text-sm text-gray-400 hover:text-white">Back to Home</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <nav className="bg-black/30 border-b border-white/10 sticky top-0 z-50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-purple-400" />
            <span className="font-bold text-white text-lg">LMS - Notes Manager</span>
            <Badge className="bg-purple-600/30 text-purple-300 border-purple-500/50 text-xs">{VERSION}</Badge>
          </div>
          <div className="flex items-center gap-3">
            {/* Credits Counter */}
            <div className="flex items-center gap-1.5 bg-green-500/20 px-3 py-1.5 rounded-full border border-green-500/30">
              <DollarSign className="w-4 h-4 text-green-400" />
              <span className="text-green-400 font-mono text-sm">${totalCredits.toFixed(3)}</span>
            </div>
            <Link to="/admin">
              <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
                Admin
              </Button>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
                <Home className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Live Generation Status Banner */}
        {generationStatus && generationStatus.total_active > 0 && (
          <Card className="bg-gradient-to-r from-blue-900/50 to-cyan-900/50 border-blue-500/30 mb-4">
            <CardContent className="py-3">
              <div className="flex items-center gap-3">
                <Activity className="w-5 h-5 text-blue-400 animate-pulse" />
                <span className="text-blue-300 font-medium">Live Generation:</span>
                <Badge className="bg-blue-500/30 text-blue-200">
                  {generationStatus.active_single_jobs?.length || 0} Single Jobs
                </Badge>
                <Badge className="bg-cyan-500/30 text-cyan-200">
                  {generationStatus.active_bulk_jobs?.length || 0} Bulk Jobs
                </Badge>
              </div>
              {generationStatus.active_single_jobs?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {generationStatus.active_single_jobs.slice(0, 5).map((job, i) => (
                    <Badge key={i} className="bg-blue-500/20 text-blue-200 text-xs">
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      {job.topic?.substring(0, 30)}... ({job.language})
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Global Actions Card */}
        <Card className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-purple-500/30 mb-6">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              {/* Stats */}
              <div className="flex flex-wrap items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-400" />
                  <span className="text-gray-400">Total:</span>
                  <span className="text-white font-bold">{stats?.total_cached || 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-blue-400" />
                  <span className="text-gray-400">EN:</span>
                  <span className="text-white font-bold">{stats?.by_language?.english || 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Languages className="w-4 h-4 text-orange-400" />
                  <span className="text-gray-400">HI:</span>
                  <span className="text-white font-bold">{stats?.by_language?.hindi || 0}</span>
                </div>
                {pendingCount && (
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-400" />
                    <span className="text-gray-400">Pending:</span>
                    <span className="text-amber-400 font-bold">{pendingCount.total_pending_notes}</span>
                  </div>
                )}
                {notesWithoutImages && notesWithoutImages.total > 0 && (
                  <div className="flex items-center gap-2">
                    <Image className="w-4 h-4 text-pink-400" />
                    <span className="text-gray-400">No Images:</span>
                    <span className="text-pink-400 font-bold">{notesWithoutImages.total}</span>
                  </div>
                )}
                {hindiIssues && hindiIssues.notes_with_issues > 0 && (
                  <div className="flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-400" />
                    <span className="text-gray-400">Hindi Issues:</span>
                    <span className="text-red-400 font-bold">{hindiIssues.notes_with_issues}</span>
                  </div>
                )}
              </div>
              
              {/* Global Action Buttons */}
              <div className="flex flex-wrap gap-2">
                {pendingCount && pendingCount.total_pending_notes > 0 && (
                  <Button
                    onClick={createAllPendingNotes}
                    disabled={bulkCreatingSubject !== null}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                    data-testid="create-all-pending-btn"
                  >
                    {bulkCreatingSubject?.exam_type === "ALL" ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating All...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" />Create ALL ({pendingCount.total_pending_notes})</>
                    )}
                  </Button>
                )}
                {notesWithoutImages && notesWithoutImages.total > 0 && (
                  <Button
                    onClick={addImagestoAllNotes}
                    disabled={bulkCreatingSubject !== null}
                    variant="outline"
                    className="border-pink-500/50 text-pink-400 hover:bg-pink-500/20"
                    data-testid="add-all-images-btn"
                  >
                    {bulkCreatingSubject?.exam_type === "ADD_IMAGES" ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Adding Images...</>
                    ) : (
                      <><ImagePlus className="w-4 h-4 mr-2" />Add Images ({notesWithoutImages.total})</>
                    )}
                  </Button>
                )}
                {hindiIssues && hindiIssues.notes_with_issues > 0 && (
                  <Button
                    onClick={fixHindiNotes}
                    disabled={bulkCreatingSubject !== null}
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                    data-testid="fix-hindi-btn"
                  >
                    {bulkCreatingSubject?.exam_type === "FIX_HINDI" ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Fixing...</>
                    ) : (
                      <><RefreshCw className="w-4 h-4 mr-2" />Fix Hindi ({hindiIssues.notes_with_issues})</>
                    )}
                  </Button>
                )}
              </div>
            </div>
            
            {/* Global Progress Bar */}
            {bulkJobProgress && (bulkCreatingSubject?.exam_type === "ALL" || bulkCreatingSubject?.exam_type === "FIX_HINDI") && (
              <div className="mt-4 p-4 bg-black/20 rounded-lg border border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-green-400" />
                    <span className="text-green-300 text-sm font-medium">
                      {bulkCreatingSubject?.exam_type === "FIX_HINDI" ? "Fixing Hindi notes..." : "Creating ALL notes..."}
                    </span>
                  </div>
                  <span className="text-green-400 font-mono text-sm">
                    {bulkJobProgress.completed}/{bulkJobProgress.total} | ${bulkJobProgress.credits_used?.toFixed(3)}
                  </span>
                </div>
                <div className="w-full bg-green-900/50 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${(bulkJobProgress.completed / bulkJobProgress.total) * 100}%` }}
                  />
                </div>
                {bulkJobProgress.current_topic && (
                  <p className="text-xs text-gray-400 mt-2">Current: {bulkJobProgress.current_topic}</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Selection Controls */}
        <Card className="bg-white/5 border-white/10 mb-6">
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm text-gray-400 mb-1 block">Exam Type</label>
                <Select value={examType} onValueChange={(v) => { setExamType(v); setSubject(""); setTopics([]); }}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="lms-exam-select">
                    <SelectValue placeholder="Select Exam" />
                  </SelectTrigger>
                  <SelectContent>
                    {examTypes.map(e => (
                      <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm text-gray-400 mb-1 block">Subject</label>
                <Select value={subject} onValueChange={setSubject} disabled={!examType}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="lms-subject-select">
                    <SelectValue placeholder="Select Subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjects.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={fetchTopics} disabled={!examType || !subject || loading} variant="outline" className="border-white/20 text-white hover:bg-white/10">
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions */}
        {topics.length > 0 && (
          <Card className="bg-white/5 border-white/10 mb-6">
            <CardContent className="py-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-400">
                    <span className="text-white font-bold">{createdCount}</span>/{totalTopics} complete | 
                    <span className="text-amber-400 font-bold ml-1">{uncreatedCount}</span> pending
                  </div>
                  <div className="text-sm text-gray-400">
                    Selected: <span className="text-purple-400 font-bold">{selectedTopics.size}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={selectAllUncreated} variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
                    Select All Pending
                  </Button>
                  <Button onClick={clearSelection} variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
                    Clear
                  </Button>
                  <Button 
                    onClick={createAllSelected} 
                    disabled={selectedTopics.size === 0 || isCurrentSubjectBulkCreating}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                    data-testid="create-all-btn"
                  >
                    {isCurrentSubjectBulkCreating ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" />Create All ({selectedTopics.size})</>
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Bulk Progress Bar - only show for current subject */}
              {bulkJobProgress && isCurrentSubjectBulkCreating && (
                <div className="mt-4 p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
                      <span className="text-purple-300 text-sm font-medium">
                        Creating notes in background...
                      </span>
                    </div>
                    <span className="text-purple-400 font-mono text-sm">
                      {bulkJobProgress.completed}/{bulkJobProgress.total} | ${bulkJobProgress.credits_used?.toFixed(3)}
                    </span>
                  </div>
                  <div className="w-full bg-purple-900/50 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${(bulkJobProgress.completed / bulkJobProgress.total) * 100}%` }}
                    />
                  </div>
                  {bulkJobProgress.current_topic && (
                    <p className="text-xs text-gray-400 mt-2">
                      Current: {bulkJobProgress.current_topic}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    ✨ Using Content Curator AI for quality validation • Safe to close tab
                  </p>
                </div>
              )}
              
              {/* Show notification if there's a job running for a DIFFERENT subject */}
              {bulkJobProgress && bulkCreatingSubject && !isCurrentSubjectBulkCreating && (
                <div className="mt-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                  <div className="flex items-center gap-2 text-blue-300 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>
                      Background job running: <strong>{bulkCreatingSubject.exam_type.toUpperCase()} {bulkCreatingSubject.subject.replace(/_/g, ' ')}</strong>
                      ({bulkJobProgress.completed}/{bulkJobProgress.total})
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Topics List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
          </div>
        ) : topics.length > 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardHeader className="border-b border-white/10 py-3 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-purple-400" />
                  {examType.toUpperCase()} - {subject.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </CardTitle>
                <Badge variant="outline" className="border-purple-500/50 text-purple-400">
                  {topics.length} Topics
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-white/5">
                {topics.map((topic, index) => {
                  const isSelected = selectedTopics.has(topic.topic);
                  const needsCreation = !topic.english || !topic.hindi;
                  const processing = processingTopics.get(topic.topic);
                  const isProcessing = processing && (processing.english === 'loading' || processing.hindi === 'loading');
                  
                  return (
                    <div 
                      key={topic.topic}
                      className={`flex items-center gap-4 px-4 py-3 hover:bg-white/5 transition ${isSelected ? 'bg-purple-500/10' : ''}`}
                      data-testid={`topic-row-${index}`}
                    >
                      {/* Checkbox */}
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleTopic(topic.topic)}
                        className="border-white/30 data-[state=checked]:bg-purple-600"
                        disabled={isProcessing}
                      />
                      
                      {/* Index */}
                      <span className="text-gray-500 text-sm w-8">{index + 1}.</span>
                      
                      {/* Topic Name */}
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{topic.topic}</p>
                      </div>
                      
                      {/* Status Indicators */}
                      <div className="flex items-center gap-4">
                        {/* English Status */}
                        <div className="flex items-center gap-1.5 min-w-[80px]">
                          {getStatusIcon(topic.topic, 'english')}
                          <span className="text-xs text-gray-400">EN</span>
                          {topic.english && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-1 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                              onClick={() => rewriteNote(topic.topic, 'english')}
                              disabled={isProcessing}
                              title="Rewrite English"
                            >
                              <RefreshCw className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                        
                        {/* Hindi Status */}
                        <div className="flex items-center gap-1.5 min-w-[80px]">
                          {getStatusIcon(topic.topic, 'hindi')}
                          <span className="text-xs text-gray-400">HI</span>
                          {topic.hindi && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-1 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                              onClick={() => rewriteNote(topic.topic, 'hindi')}
                              disabled={isProcessing}
                              title="Rewrite Hindi"
                            >
                              <RefreshCw className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {/* View Notes Buttons */}
                        {topic.english && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                            onClick={() => viewNote(topic.topic, 'english')}
                            title="View English Notes (Opens Student Page)"
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            EN
                          </Button>
                        )}
                        {topic.hindi && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                            onClick={() => viewNote(topic.topic, 'hindi')}
                            title="View Hindi Notes (Opens Student Page)"
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            HI
                          </Button>
                        )}
                        
                        {/* Add Images Button */}
                        {(topic.english || topic.hindi) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-pink-400 hover:text-pink-300 hover:bg-pink-500/10"
                            onClick={() => addImagesToNote(topic.topic, topic.english ? 'english' : 'hindi')}
                            disabled={addingImages === `${topic.topic}-${topic.english ? 'english' : 'hindi'}`}
                            title="Add Images"
                          >
                            {addingImages === `${topic.topic}-${topic.english ? 'english' : 'hindi'}` ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <ImagePlus className="w-3 h-3" />
                            )}
                          </Button>
                        )}
                        
                        {needsCreation ? (
                          <Button
                            size="sm"
                            className="bg-purple-600 hover:bg-purple-700 h-8"
                            onClick={() => createNote(topic.topic)}
                            disabled={isProcessing}
                            data-testid={`create-btn-${index}`}
                          >
                            {isProcessing ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Sparkles className="w-3 h-3 mr-1" />
                                Create
                              </>
                            )}
                          </Button>
                        ) : (
                          <Badge className="bg-green-600/20 text-green-400 border-green-500/30">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Complete
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ) : examType && subject ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-600" />
              <p className="text-gray-400">No topics found for this subject</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="py-12 text-center">
              <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-600" />
              <p className="text-gray-400">Select an exam type and subject to view topics</p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
